import { CommonModule, formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@app/core/api/auth/auth.service';
import { InvestListResponse, InvestService, InvestUserRequest } from '@app/core/api/users/invest.service';
import { OnlineUsersService } from '@app/core/services/online-users.service';
import { ColumnDef, DataTableComponent, PaginationMeta, RowAction } from '@app/shared/components/data-table/data-table.component';
import { ModalService } from '@app/shared/components/modal/modal.service';
import { ToastService } from '@app/shared/components/toast/toast.service';
import { ButtonComponent } from '@app/shared/forms/button/button.component';
import { LucideAngularModule } from 'lucide-angular';
import { Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-connection-requests',
  standalone: true,
  imports: [DataTableComponent, LucideAngularModule, CommonModule, ButtonComponent],
  templateUrl: './connection-requests.component.html',
  styleUrl: './connection-requests.component.scss'
})
export class ConnectionRequestsComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);
  investService = inject(InvestService);
  modalService = inject(ModalService);
  toastService = inject(ToastService);
  private router = inject(Router);

  @ViewChild('connectionModal') connectionModal!: TemplateRef<any>;
  @ViewChild('connectionDisconnectModal') connectionDisconnectModal!: TemplateRef<any>;
  @ViewChild('muteModal') muteModal!: TemplateRef<any>;

  destroy$ = new Subject<void>();

  connectionColumn: ColumnDef[] = [
    { key: 'request', label: 'Request' },
    {
      key: 'date_time',
      label: 'Date and Time',
      valueFn: (row) => formatDate(row.updated_at, 'MM/dd/yyyy hh:mm a', 'en-US'),
    }
  ];

  actionsDefault: RowAction[] = [
    {
      id: 1,
      label: "Accept",
      tooltip: "Accept connection request",
      callback: (row) => this.pending(row),
      show: (row) => row.status_id == 'pending' ? true : false,
      buttonClass: 'px-5 py-2.5 text-white button button--primary rounded hover:button--primary-80',
    },
    {
      id: 2,
      label: "Decline",
      tooltip: "Decline connection request",
      callback: (row) => this.revoke(row),
      show: (row) => row.status_id == 'pending' ? true : false,
      buttonClass: 'px-5 py-2.5 text-white button bg-tertiary rounded hover:bg-red-600'
    },
    {
      id: 3,
      label: (row) => row.isMuted ? "Unmute" : "Mute",
      callback: (row) => row.isMuted ? this.unmute(row) : this.mute(row),
      show: true,
      buttonClass: 'px-5 py-2.5 text-black button button--secondary rounded hover:button--secondary-80',
    }
  ];

  page = 1;
  loading = false;
  isSubmitting = false;
  requests: any[] = [];
  currentUser: any;
  type: 'Investor' | 'Investee' = "Investor";
  isMuted = false;
  meta: PaginationMeta = {
    from: 0,
    to: 0,
    total: 0,
    current_page: 1,
    last_page: 1,
  };

  ngOnInit() {
    this.getCurrentUser();
  }

  getCurrentUser() {
    this.authService.getCurrentUser()
      .pipe(take(1))
      .subscribe({
        next: (user) => {
          this.currentUser = user.data;
          this.type = this.currentUser?.type == 'Investor' ? 'Investee' : 'Investor';

          this.loadRequests();
        },
        error: (err) => console.error('Error fetching current user:', err)
      });
  }

  loadRequests() {
    this.loading = true;
    let params = new HttpParams().set('page', this.page.toString());
    params = params.set('receiver_id', this.currentUser?.invest_id)
    params = params.set('status_id', 'pending');

    this.investService
      .getAllRequests(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: InvestListResponse) => {
          this.requests = res.data.map((row, i) => {
            const name = this.type == 'Investor' ? row.investor?.corporate_name : row.investee?.corporate_name ?? 'N/A';
            const code = this.type == 'Investor' ? row.investor?.code : row.investee?.code ?? 'N/A';

            return {
              ...row,
              request: `${this.type} ${this.type == 'Investor' ? `${name}` : `${code}`} wants to connect with you`,
              code,
              name,
              date_time: row.updated_at,
              isMuted: this.type == 'Investor' ? row.investee_muted : row.investor_muted
            };
          });


          this.meta = this.getMeta(res);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  getMeta(res: any): PaginationMeta {
    return {
      from: res.meta?.from ?? 0,
      to: res.meta?.to ?? 0,
      total: res.meta?.total ?? 0,
      current_page: res.meta?.current_page ?? 1,
      last_page: res.meta?.last_page ?? 1,
    };
  }

  pending(row: any) {
    let request: Partial<InvestUserRequest> = {
      investor_id: row.investor?.id,
      investee_id: row.investee?.id,
      receiver_id: row.receiver
    };

    const connectionModal = this.modalService.open({
      title: 'Connection Request',
      contentTemplate: this.connectionModal,
      contentData: {
        onAcceptRequest: () => {
          this.accept(request, row);
          this.modalService.close(connectionModal);
        },
        onCancel: () => {
          this.modalService.close(connectionModal);
        },
      },
      position: 'center',
      animation: 'fade',
      showCloseButton: false,
      backdropDismiss: false,
      keyboardClose: false
    });
  }

  accept(request: Partial<InvestUserRequest>, row: any) {
    this.loading = true;
    request.status_id = "accepted";
    this.investService.acceptRequest(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.toastService.show({
            title: 'Success',
            content: `The request to connect from ${this.type == 'Investor' ? `${row.name}` : `${row.code}`} has been accepted.`,
            type: 'success',
            duration: 5000,
          });
          this.loading = false;
          this.getCurrentUser();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  revoke(row: any) {
    let request: Partial<InvestUserRequest> = {
      investor_id: row.investor?.id,
      investee_id: row.investee?.id,
      receiver_id: row.receiver
    };

    const connectionDisconnectModal = this.modalService.open({
      title: 'Connection Request',
      contentTemplate: this.connectionDisconnectModal,
      contentData: {
        onConfirmRequest: () => {
          this.disconnect(request, row);
          this.modalService.close(connectionDisconnectModal);
        },
        onCancel: () => {
          this.modalService.close(connectionDisconnectModal);
        },
      },
      position: 'center',
      animation: 'fade',
      showCloseButton: false,
      backdropDismiss: false,
      keyboardClose: false
    });
  }

  disconnect(request: Partial<InvestUserRequest>, row: any) {
    this.loading = true;
    request.status_id = "declined";
    this.investService.disconnect(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.toastService.show({
            title: 'Success',
            content: response.message ?? `The request to connect from ${this.type == 'Investor' ? `${row.name}` : `${row.code}`} has been denied.`,
            type: 'success',
            duration: 5000,
          });
          this.loading = false;
          this.getCurrentUser();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  reject(row: any) {
    alert(JSON.stringify(row))
  }

  mute(row: any) {
    const request: Partial<InvestUserRequest> = {
      investor_id: row.investor?.id,
      investee_id: row.investee?.id,
      muted_by: this.currentUser?.invest_id
    }

    const modal = this.modalService.open({
      title: `Mute ${this.type}`,
      contentTemplate: this.muteModal,
      contentData: {
        onConfirm: () => {
          this.loading = true;
          this.isSubmitting = true;
          this.investService.mute(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.toastService.show({
                  title: 'Success',
                  content: `You have muted this ${this.type}`,
                  type: 'success',
                  duration: 3000,
                });

                this.modalService.close(modal);
                this.loadRequests();
                this.loading = false;
                this.isSubmitting = false;
              },
              error: () => {
                this.loading = false;
                this.isSubmitting = false;
                this.modalService.close(modal);
              },
            });
        },
        onCancel: () => {
          this.modalService.close(modal);
          this.loading = false;
        },
      },
      showCloseButton: false,
      backdropDismiss: false,
      keyboardClose: false
    });
  }

  unmute(row: any) {
    const request: Partial<InvestUserRequest> = {
      investor_id: row.investor?.id,
      investee_id: row.investee?.id,
      muted_by: this.currentUser?.invest_id
    }

    const modal = this.modalService.open({
      title: `Unmute ${this.type}`,
      contentTemplate: this.muteModal,
      contentData: {
        onConfirm: () => {
          this.loading = true;
          this.isSubmitting = true;
          this.investService.unmute(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.toastService.show({
                  title: 'Success',
                  content: `You have unmuted this ${this.type}`,
                  type: 'success',
                  duration: 5000,
                });

                this.modalService.close(modal);
                this.loadRequests();
                this.loading = false;
                this.isSubmitting = false;
              },
              error: () => {
                this.loading = false;
                this.isSubmitting = false;
                this.modalService.close(modal);
              },
            });
        },
        onCancel: () => {
          this.modalService.close(modal);
          this.loading = false;
        },
      },
      showCloseButton: false,
      backdropDismiss: false,
      keyboardClose: false
    });
  }

  onRowAction({ action, row }: { action: RowAction; row: any }) {
    if (action.callback) action.callback(row);
  }

  onPageChange(newPage: number) {
    this.page = newPage;
    this.loadRequests();
  }

  onRecordChange() {
    this.page = 1;
    this.loadRequests();
  }

  get columns(): ColumnDef[] {
    return this.connectionColumn;
  }
  get actions(): RowAction[] {
    return this.actionsDefault;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
